import { useEffect, useMemo, useState } from "react";
import { useLocation } from "react-router-dom";
import type { FestiveEventDto } from "@hrms/types";

import { api, getAccessToken } from "../lib/api.js";
import { logError } from "../lib/logger.js";
import { toFestival, type Festival } from "../lib/festive/festivals.js";
import {
  festivalPreview,
  getActiveFestival,
  type ActiveFestival,
} from "../lib/festive/getActiveFestival.js";

const REDUCED_MOTION = "(prefers-reduced-motion: reduce)";
const CACHE_MS = 5 * 60 * 1000;

/**
 * The QA override works in dev, and in any build made with VITE_FESTIVE_PREVIEW=true. In other
 * builds the URL parameters are ignored.
 */
export const festivePreviewEnabled = (): boolean =>
  import.meta.env.DEV || import.meta.env["VITE_FESTIVE_PREVIEW"] === "true";

/** One request is shared by every component on the page. It is refetched when the token changes. */
let cache: { token: string | null; loadedAt: number; promise: Promise<Festival[]> } | null = null;

/**
 * The events this viewer can see. Signed in, the server filters by the viewer's branch. On the
 * login page (no session yet) it returns only the all-branch events.
 */
function loadFestivals(): Promise<Festival[]> {
  const token = getAccessToken();
  if (cache && cache.token === token && Date.now() - cache.loadedAt < CACHE_MS) return cache.promise;

  const path = token ? "/festive-events/visible" : "/festive-events/public";
  const promise = api
    .get<FestiveEventDto[]>(path)
    .then((r) => r.data.map(toFestival))
    .catch((err) => {
      cache = null;
      logError("useActiveFestival.load", err);
      return [];
    });
  cache = { token, loadedAt: Date.now(), promise };
  return promise;
}

function useFestivalList(): Festival[] {
  const [festivals, setFestivals] = useState<Festival[]>([]);
  useEffect(() => {
    let cancelled = false;
    void loadFestivals().then((list) => {
      if (!cancelled) setFestivals(list);
    });
    return () => {
      cancelled = true;
    };
  }, []);
  return festivals;
}

/** Milliseconds from `now` to the next local midnight, plus a second of margin. */
function msUntilNextMidnight(now: Date): number {
  const next = new Date(now.getFullYear(), now.getMonth(), now.getDate() + 1);
  return next.getTime() - now.getTime() + 1000;
}

/**
 * The festival showing today, or null. It is memoised, and it re-evaluates at the next local
 * midnight, so a tab left open updates itself. There is no polling and no animation loop.
 */
export function useActiveFestival(): ActiveFestival | null {
  const { search } = useLocation();
  const festivals = useFestivalList();
  const [today, setToday] = useState(() => new Date());

  useEffect(() => {
    const timer = window.setTimeout(() => setToday(new Date()), msUntilNextMidnight(today));
    return () => window.clearTimeout(timer);
  }, [today]);

  const computed = useMemo(() => getActiveFestival(today, festivals), [today, festivals]);
  const preview = festivePreviewEnabled() ? festivalPreview(search, festivals) : null;
  return preview ? preview.active : computed;
}

/**
 * True when the OS asks for reduced motion, unless the QA override `?motion=on` is set. The
 * badge then shows its still icon. Live changes to the OS setting are followed.
 */
export function useFestiveReducedMotion(): boolean {
  const { search } = useLocation();
  const [osReduce, setOsReduce] = useState(() => window.matchMedia(REDUCED_MOTION).matches);

  useEffect(() => {
    const query = window.matchMedia(REDUCED_MOTION);
    const onChange = () => setOsReduce(query.matches);
    query.addEventListener("change", onChange);
    return () => query.removeEventListener("change", onChange);
  }, []);

  const forcedOn = festivePreviewEnabled() && new URLSearchParams(search).get("motion") === "on";
  return osReduce && !forcedOn;
}
